import { useEffect, useState } from 'react'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { showError } from '@/lib/utils/notifications'
import { getAgentsForProperty } from '@/lib/api/propertyService'
import {
  buildAgentOptionsFromRows,
  formatAgentPhoneValue,
  getAgentPrefillForSavedProperty,
  getSavedPropertyId,
  getSingleAgentAutofill,
  resolveInitialAgentId,
  shouldApplySingleAgentAutofill,
} from '../components/tabs/propertyTabHelpers'
import type {
  PropertyAgentRow,
  PropertyTabFormChangePayload,
  PropertyTabLocalFormData,
} from '../types/tabs'
import type { ApplicationDetailData } from '../types/application'

export function usePropertyTabAgents({
  isEdit,
  formData,
  setFormData,
  applicationAllData,
  onFormChange,
  lastSyncedPropertyIdRef,
}: {
  isEdit: boolean
  formData: PropertyTabLocalFormData
  setFormData: React.Dispatch<React.SetStateAction<PropertyTabLocalFormData>>
  applicationAllData: ApplicationDetailData
  onFormChange?: (formData: PropertyTabFormChangePayload) => void
  lastSyncedPropertyIdRef: React.MutableRefObject<string>
}) {
  const [agentOptions, setAgentOptions] = useState<SearchableSelectOption[]>([])
  const [agentsById, setAgentsById] = useState<Record<string, PropertyAgentRow>>({})
  const [loadingAgents, setLoadingAgents] = useState(false)

  useEffect(() => {
    if (!isEdit || formData.has_applied !== true) return
    const propertyId = formData.property_id
    if (!propertyId) return
    const inviteCode = String(applicationAllData?.invite_code ?? '')
    const savedPropertyId = getSavedPropertyId(applicationAllData)
    const isSameProperty =
      savedPropertyId !== '' && savedPropertyId === String(propertyId)
    const propertyChanged =
      lastSyncedPropertyIdRef.current !== '' &&
      lastSyncedPropertyIdRef.current !== String(propertyId)
    lastSyncedPropertyIdRef.current = String(propertyId)

    let mounted = true
    setLoadingAgents(true)
    getAgentsForProperty(propertyId, inviteCode)
      .then((rows) => {
        if (!mounted) return

        const currentAgentId =
          propertyChanged && !isSameProperty
            ? ''
            : String(
                formData.agent_id ||
                  resolveInitialAgentId(applicationAllData) ||
                  '',
              )
        const { byId, opts } = buildAgentOptionsFromRows(
          rows,
          applicationAllData,
          currentAgentId,
        )
        setAgentsById(byId)
        setAgentOptions(opts)

        const prefill = isSameProperty
          ? getAgentPrefillForSavedProperty(applicationAllData)
          : null
        const singleAgent = getSingleAgentAutofill(rows)
        let notifySingleAgentId = ''

        setFormData((prev) => {
          const applySingle =
            singleAgent &&
            shouldApplySingleAgentAutofill(String(prev.agent_id ?? ''), prefill)

          if (propertyChanged && !isSameProperty && !applySingle) {
            return {
              ...prev,
              agent_id: '',
              agent_name: '',
              agent_email: '',
              agent_phone: '',
            }
          }

          if (prefill) {
            const nextId = prefill.agent_id
            const fromList = nextId !== 'Other' ? byId[nextId] : undefined
            return {
              ...prev,
              agent_id: nextId,
              agent_name: nextId === 'Other' ? prefill.agent_name : '',
              agent_email: String(fromList?.email ?? prefill.agent_email ?? ''),
              agent_phone: formatAgentPhoneValue(
                fromList?.mobile_no ?? prefill.agent_phone,
              ),
            }
          }

          if (applySingle && singleAgent) {
            notifySingleAgentId = singleAgent.agent_id
            return {
              ...prev,
              agent_id: singleAgent.agent_id,
              agent_name: '',
              agent_email: singleAgent.agent_email,
              agent_phone: formatAgentPhoneValue(singleAgent.agent_phone),
            }
          }

          const id = String(prev.agent_id ?? '')
          if (id && id !== 'Other' && byId[id]) {
            const selected = byId[id]
            return {
              ...prev,
              agent_name: '',
              agent_email: String(selected?.email ?? prev.agent_email ?? ''),
              agent_phone: formatAgentPhoneValue(
                selected?.mobile_no ?? prev.agent_phone,
              ),
            }
          }

          return prev
        })

        if (propertyChanged && !isSameProperty && !notifySingleAgentId) {
          onFormChange?.({
            has_applied: formData.has_applied,
            applied_property: formData.has_applied === false ? '0' : '1',
            fk_property_id: String(propertyId),
          })
        } else if (prefill) {
          if (prefill.agent_id === 'Other') {
            onFormChange?.({
              has_applied: formData.has_applied,
              applied_property: formData.has_applied === false ? '0' : '1',
              fk_property_id: String(propertyId),
              other_agent: {
                agent_name: prefill.agent_name,
                agent_email: prefill.agent_email,
                agent_mobile_no: prefill.agent_phone,
              },
            })
          } else if (prefill.agent_id) {
            onFormChange?.({
              has_applied: formData.has_applied,
              applied_property: formData.has_applied === false ? '0' : '1',
              fk_property_id: String(propertyId),
              fk_agent_id: prefill.agent_id,
            })
          }
        } else if (notifySingleAgentId) {
          onFormChange?.({
            has_applied: formData.has_applied,
            applied_property: formData.has_applied === false ? '0' : '1',
            fk_property_id: String(propertyId),
            fk_agent_id: notifySingleAgentId,
          })
        }
      })
      .catch((e) => {
        if (!mounted) return
        showError((e as { message?: string })?.message || 'Failed to load agents.')
        setAgentsById({})
        setAgentOptions([{ value: 'Other', label: 'Other' }])
      })
      .finally(() => {
        if (!mounted) return
        setLoadingAgents(false)
      })

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, formData.has_applied, formData.property_id, applicationAllData?.invite_code])

  const applyAgentSelection = (nextAgentId: string) => {
    const cleaned = String(nextAgentId ?? '')
    if (!cleaned) {
      setFormData((prev) => ({
        ...prev,
        agent_id: '',
        agent_name: '',
        agent_email: '',
        agent_phone: '',
      }))
      onFormChange?.({
        has_applied: formData.has_applied,
        applied_property: formData.has_applied === false ? '0' : '1',
        fk_property_id: formData.property_id,
      })
      return
    }

    if (cleaned === 'Other') {
      const other = applicationAllData?.other_agent ?? {}
      setFormData((prev) => ({
        ...prev,
        agent_id: 'Other',
        agent_name: String(other?.agent_name ?? prev.agent_name ?? ''),
        agent_email: String(other?.agent_email ?? prev.agent_email ?? ''),
        agent_phone: formatAgentPhoneValue(
          other?.agent_mobile_no ?? prev.agent_phone,
        ),
      }))
      onFormChange?.({
        has_applied: formData.has_applied,
        applied_property: formData.has_applied === false ? '0' : '1',
        fk_property_id: formData.property_id,
        other_agent: {
          agent_name: String(other?.agent_name ?? ''),
          agent_email: String(other?.agent_email ?? ''),
          agent_mobile_no: String(other?.agent_mobile_no ?? ''),
        },
      })
      return
    }

    const selected = agentsById[cleaned]
    setFormData((prev) => ({
      ...prev,
      agent_id: cleaned,
      agent_name: '',
      agent_email: String(selected?.email ?? ''),
      agent_phone: formatAgentPhoneValue(selected?.mobile_no),
    }))
    onFormChange?.({
      has_applied: formData.has_applied,
      applied_property: formData.has_applied === false ? '0' : '1',
      fk_property_id: formData.property_id,
      fk_agent_id: cleaned,
    })
  }

  return {
    agentOptions,
    loadingAgents,
    applyAgentSelection,
  }
}
